import React, { useMemo, useCallback, useState } from 'react'
import { Stack, Typography, Chip } from '@mui/material'
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import { useIsMobile } from '@/hooks/Responsive'
import { userSend } from 'api/users';
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

export function createNoticeTableColumns({ isMobile, onTitleClick, lang, paramData, searchYn }) {
  // 번호 컬럼
  const renderNo = (v, row) => {
    const noticeYn = String(row.noticeYn ?? '').trim()
    const vlab = row.vlab

    let label = null
    let labelColor = 'text.primary'

    if (vlab === "true" ) {
      label = 'Vlab'
      labelColor = 'primary.main'
    } else if (!vlab && noticeYn === 'Y') {
      label = '중요'
      labelColor = 'primary.main'
    }

    if (label) {
      return (
        <Typography
          fontWeight={600}
          color={labelColor}
          sx={{ textAlign: isMobile ? 'left' : 'center' }}
        >
          {label}
        </Typography>
      )
    }

    // 기본 (일반글: 숫자 번호)
    return (
      <Typography sx={{ textAlign: isMobile ? 'left' : 'center' }}>
        {v}
      </Typography>
    )
  }

  const renderTitle = (v, row) => {
    const noticeYn = String(row.noticeYn ?? '').trim()
    const upendYn = String(row.upendYn ?? '').trim();

    let showChip = false
    let chipColor = ''

    // if (noticeYn === '공지') {
    if (noticeYn === 'Y' && upendYn !== 'Y') {
      showChip = true
      chipColor = 'grey'
    // } else if (noticeYn === '전체') {
    } else if (upendYn === 'Y') {
      showChip = true
      chipColor = 'primary'
    }

    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        spacing={1}
      >
        {/* 공지/전체만 칩 표시 */}
        {showChip && (
          <Chip
            label={upendYn === 'Y' ? '전체' : noticeYn === 'Y' ? '공지' : '' }
            variant="outlined"
            color={chipColor}
          />
        )}

        {/* 제목 텍스트 */}
        <Typography
          component={RouterLink}
          to={buildLangHref('/alarm/noticeview', lang)}
          state={{
            searchYn,
            navParamData: paramData,
            rowData: row,
          }}
          variant={isMobile ? 'subtitle3' : 'body1'}
          fontWeight={isMobile ? '600' : '400'}
          sx={{
            cursor: 'pointer',
            overflow: isMobile ? 'visible' : 'hidden',
            textOverflow: isMobile ? 'unset' : 'ellipsis',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            wordBreak: 'keep-all',
            '&:hover': { textDecoration: 'underline', fontWeight: '700' },
          }}
        >
          {v}
        </Typography>

      </Stack>
    )
  }

  const renderAttachments = (v) => {
    // const hasFile = Array.isArray(v) && v.length > 0
    const hasFile = (v === "Y" ? true : false)
    if (!hasFile) return undefined 
    
    return (
      <AttachFileIcon
        aria-label="첨부파일 있음"
        sx={{
          color: 'grey.dark',
          verticalAlign: 'middle',
        }}
      />
    )
  }

  return [
    { field: 'rowNum', role: 'no', headerName: '번호', sortable: false, width: '96px', align: 'center', render: renderNo, hideTitleInCard: true },
    { field: 'bbsTitle', role: 'title', headerName: '제목', sortable: false, align: 'left',  render: renderTitle, hideTitleInCard: true },
    { field: 'registDttm', role: 'date', headerName: '등록일', sortable: false, width: '180px', align: 'center', hideTitleInCard: true },
    { field: 'attYn', role: 'attachment', headerName: '첨부파일', sortable: false, width: '120px', align: 'center', render: renderAttachments, hideTitleInCard: true },
  ]
}

export default function Notice() {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useLang()
  const { isMobile } = useIsMobile()
  const [loading, setLoading] = useState(false)
  
  const [page, setPage] = useState(1);  // 현재 페이지
  const pageSize        = 10;           // 페이지 크기
  const recordSize      = 10;           // 레코드 크기

  const navParamData = location.state?.navParamData;
  const [searchYn, setSearchYn] = useState(location.state?.searchYn || "N");
  const [paramData, setParamData] = useState({
      page : page,
      pageSize : pageSize,
      recordSize: recordSize,
      searchNm : "",
  })
  const [confirmedSearch, setConfirmedSearch] = useState('') 

  // const handleTitleClick = useCallback((rowData) => {
  //   navigate(buildLangHref('/alarm/noticeview', lang), { state : { searchYn : searchYn, navParamData: paramData, rowData : rowData } });
  // }, [navigate])


  // 검색버튼 클릭
  const handleSearch = (v) => {
    setConfirmedSearch(v);
  }

  const fetcher = useCallback(
    async ({ page, pageSize }) => {
      setLoading(true);

      const params = {
        page : page,
        pageSize : pageSize,
        recordSize: recordSize,
        searchNm : confirmedSearch,
      }
    
      const res = await userSend('/alarm/getNoticeList', params);
      setLoading(false)  // 데이터 로딩 끝

      return {
        items : res.data?.content,
        total : res.data?.totalElements
      }

    }, [confirmedSearch])

  const columns = useMemo(() =>
    createNoticeTableColumns({ isMobile, lang, paramData, searchYn }),
  [isMobile, lang, paramData, searchYn])

  return (
    <BoardList
      view="table"
      caption="공지사항 목록(번호, 제목, 등록일, 첨부파일로 구성)"
      columns={columns}
      fetcher={fetcher}
      TableComponent={BoardListTable}
      initialSort={{ field: 'no', direction: 'desc' }}
      initialPage={page}
      initialPageSize={pageSize}
      storageKey="setpage_notice"
    //  onRowClick={handleTitleClick}
      onSearchSubmit={(value) => handleSearch(value)}
    />
  )
}